<template>
  <v-row no-gutters>
    <v-col cols="12">
      <p class="font--m-1 mb-10 text-center">간편 회원가입</p>
      <p class="mb-1 text-left">휴대폰번호</p>
      <v-text-field
        v-model="phoneNumber"
        solo
        single-line
        hide-details
        outlined
        flat
        :error="!!invalidMsg.phoneNumber"
        type="tel"
        placeholder="예) 01012345678"
        oninput="this.value = this.value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1')"
      >
        <template v-slot:append>
          <v-fade-transition leave-absolute>
            <BasicButton
              id="btn__OnDKEVLiq0usVQz7KMOPbw=="
              height="36px"
              :loading="isLoading"
              :disabled="isValidSerialCode || phoneIsValid === false || isExpireTimeOut"
              @click="getSerialCode()"
            >
              인증번호 받기
            </BasicButton>
          </v-fade-transition>
        </template>
      </v-text-field>
      <ErrorText :text="invalidMsg.phone" nudgeLeft="4" />
    </v-col>
    <v-col cols="12" v-if="isSendSerialCode" class="position--relative mt-3">
      <p class="mb-1 text-left">인증번호</p>
      <v-text-field
        v-model="serialCode"
        solo
        single-line
        hide-details
        outlined
        flat
        :error="!!invalidMsg.serialCode"
        :disabled="isValidSerialCode"
      >
        <template v-slot:append>
          <BasicButton
            id="btn__0Y8IpR59C0ySMJT2yZKNPg=="
            height="36px"
            :loading="isLoading"
            :disabled="isLoading || isValidSerialCode || isLoading"
            @click="confirmAuthSerial()"
          >
            {{ isValidSerialCode ? '인증완료' : '인증번호 확인' }}
          </BasicButton>
        </template>
      </v-text-field>
      <ExpireTime
        v-if="authPhoneTimeoutDate"
        :expire-date="authPhoneTimeoutDate"
        @emitTimeOver="isTimeOut"
      ></ExpireTime>
      <ErrorText :text="invalidMsg.serialCode" nudgeLeft="4" />
    </v-col>
  </v-row>
</template>

<script>
import { mapActions } from 'vuex'
import Regex from '@/const/Regex'
import ExpireTime from '@/components/auth/ExpireTime.vue'

export default {
  props: {
    user: {
      type: Object,
    },
  },
  components: {
    ExpireTime,
  },
  data() {
    return {
      rules: {
        phone: [
          (value) => !!value || '휴대폰번호를 입력해주세요',
          (value) => Regex.PHONE.REGEX.test(value) || Regex.PHONE.MSG,
        ],
      },
      phoneNumber: null,
      phoneIsValid: false,
      serialCode: null,
      isSendSerialCode: false,
      isLoading: false,
      isValidSerialCode: false,
      invalidMsg: {},
      tempVerificationObj: null,
      authPhoneTimeoutDate: null,
      isExpireTimeOut: false,
    }
  },
  computed: {
    verificationCodeForm() {
      return {
        phone: this.phoneNumber,
        verificationCode: this.serialCode,
        ...this.tempVerificationObj,
      }
    },
  },
  watch: {
    phoneNumber(v) {
      this.phoneDuplicate(v)
    },
    async isValidSerialCode(v) {
      if (v === true) {
        this.$emit('update:validPhoneForm', this.verificationCodeForm)
        await this.register()
      }
    },
    authPhoneTimeoutDate(v) {
      if (v) {
        this.isExpireTimeOut = true
      } else {
        this.isExpireTimeOut = false
      }
    },
  },
  methods: {
    ...mapActions('auth', ['signinSuccess', 'getMe']),
    ...mapActions('signup', ['getVerificationCode', 'confirmVerificationCode', 'signup']),
    getFormErrMsg(type, newVal) {
      const rules = this.rules[type]
      let msg = null
      if (newVal) {
        for (let index = 0; index < rules.length; index++) {
          const rule = rules[index]
          const isValid = rule(newVal)
          if (typeof isValid !== 'boolean') {
            msg = isValid
            break
          }
        }
      }

      this.invalidMsg[type] = msg
      return msg
    },
    phoneDuplicate(phoneNumber) {
      const msg = this.getFormErrMsg('phone', phoneNumber)
      if (msg === null) {
        this.phoneIsValid = true
      } else {
        this.phoneIsValid = false
      }
    },
    async getSerialCode() {
      if (this.phoneIsValid !== true) {
        return
      }

      const confirm = await this.$swal.basic.confirm('인증번호가 발송됩니다')

      if (confirm.isConfirmed !== true) {
        return
      }

      this.isLoading = true

      try {
        const data = await this.getVerificationCode(this.phoneNumber)
        this.tempVerificationObj = {
          expirationTime: data?.expirationTime,
          token: data?.token,
        }
        this.authPhoneTimeoutDate = new Date(this.tempVerificationObj.expirationTime)
        this.isSendSerialCode = true
      } finally {
        this.isLoading = false
      }
    },
    async confirmAuthSerial() {
      if (this.isSendSerialCode !== true || this.isValidSerialCode) {
        return
      }

      this.isLoading = true

      try {
        const isValid = await this.confirmVerificationCode(this.verificationCodeForm)

        if (isValid) {
          await this.$swal.basic.alert('인증이 완료되었습니다.')
          this.isValidSerialCode = true
          this.authPhoneTimeoutDate = null
        }
      } finally {
        this.isLoading = false
      }
    },
    async isTimeOut() {
      await this.$swal.basic.alert({
        html: '발급된 인증번호의 유효기간이 만료되었습니다. <br />다시 입력해주세요.',
      })
      this.authPhoneTimeoutDate = null
    },
    async register() {
      if (this.isLoading) {
        return
      }

      const param = {
        register: {
          ...this.user,
          phone: this.verificationCodeForm?.phone,
          verification: this.verificationCodeForm,
        },
      }

      this.isLoading = true

      try {
        const user = await this.signup(param)
        if (user) {
          const { accessToken, refreshToken } = user
          this.signinSuccess({ accessToken, refreshToken })

          await this.getMe()

          this.$router.push({ name: 'my/store/register' })
        }
      } catch (e) {
        if (e.status === 400) {
          this.isValidSerialCode = null
        }
        throw e
      } finally {
        this.isLoading = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
#btn__auth-serial {
  position: absolute;
  top: 36px;
  right: 8px;
}
</style>
